import React, { useState, useEffect, useMemo } from 'react';
import { usePIM } from '../context/PIMContext';
import { Plus, Filter, Settings, Search, Calendar, CheckCircle, Clock, AlertCircle, Box, Layers, Pencil, Trash, Eye } from 'lucide-react';
import { GhostButton } from './ui/GhostButton';
import { SortableHeader } from './ui/SortableHeader';
import { useSortableData } from '../hooks/useSortableData';
import { ToDoAddModal } from './modals/ToDoAddModal';
import { ToDoGroupManagementModal } from './modals/ToDoGroupManagementModal';
import { Badge } from './ui/Badge';
import { ConfirmationModal } from './ui/ConfirmationModal';

const GlobalTasksView = ({ onClose }) => {
    const { statuses } = usePIM(); // Get statuses from context
    const [tasks, setTasks] = useState([]);
    const [groups, setGroups] = useState([]);
    const [products, setProducts] = useState([]);
    const [charges, setCharges] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');
    const [filterGroup, setFilterGroup] = useState('All');

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);

    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
        isDestructive: false
    });

    // Fetch Data
    const fetchTasks = async () => {
        try {
            const res = await fetch('/api/tasks', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (res.ok) setTasks(await res.json());
        } catch (err) { console.error("Failed to fetch tasks", err); }
    };

    const fetchGroups = async () => {
        try {
            const res = await fetch('/api/task-groups', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (res.ok) setGroups(await res.json());
        } catch (err) { console.error("Failed to fetch groups", err); }
    };

    const fetchProducts = async () => {
        try {
            const res = await fetch('/api/products', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (res.ok) setProducts(await res.json());
        } catch (err) { console.error("Failed to fetch products", err); }
    };

    const fetchCharges = async (sku) => {
        if (!sku) {
            setCharges([]);
            return;
        }
        try {
            const res = await fetch(`/api/charges?sku=${sku}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (res.ok) setCharges(await res.json());
        } catch (err) { console.error("Failed to fetch charges", err); }
    };

    useEffect(() => {
        fetchTasks();
        fetchGroups();
        fetchProducts();
    }, []);

    // Handlers
    const handleAddTodo = async (todoData) => {
        try {
            const res = await fetch('/api/tasks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify(todoData)
            });
            if (res.ok) fetchTasks();
        } catch (err) { console.error("Failed to add task", err); }
    };

    const handleEditTodo = async (id, todoData) => {
        try {
            const res = await fetch(`/api/tasks/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify(todoData)
            });
            if (res.ok) fetchTasks();
        } catch (err) { console.error("Failed to update task", err); }
    };

    const handleAddGroup = async (groupData) => {
        try {
            const res = await fetch('/api/task-groups', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify(groupData)
            });
            if (res.ok) fetchGroups();
        } catch (err) { console.error("Failed to add group", err); }
    };

    const handleUpdateGroup = async (id, groupData) => {
        try {
            const res = await fetch(`/api/task-groups/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify(groupData)
            });
            if (res.ok) fetchGroups();
        } catch (err) { console.error("Failed to update group", err); }
    };

    const handleDeleteGroup = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Group',
            message: 'Delete this group?',
            isDestructive: true,
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/task-groups/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (res.ok) fetchGroups();
                } catch (err) { console.error("Failed to delete group", err); }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleDeleteTask = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Task',
            message: 'Are you sure you want to delete this task? This action cannot be undone.',
            isDestructive: true,
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/tasks/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (res.ok) fetchTasks();
                } catch (err) { console.error("Failed to delete task", err); }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleToggleStatus = async (task) => {
        const newStatus = task.status === 'Completed' ? 'Pending' : 'Completed';
        try {
            const res = await fetch(`/api/tasks/${task.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({ ...task, status: newStatus })
            });
            if (res.ok) fetchTasks();
        } catch (err) { console.error("Failed to update status", err); }
    };

    const handleProductSelect = (productId) => {
        const product = products.find(p => p.id == productId);
        if (product) {
            fetchCharges(product.sku);
        } else {
            setCharges([]);
        }
    };

    const openEditModal = (task, readOnly = false) => {
        setTaskToEdit(task);
        setIsReadOnly(readOnly);
        if (task.product_id) {
            const product = products.find(p => p.id === task.product_id);
            if (product) fetchCharges(product.sku);
        }
        setIsAddModalOpen(true);
    };

    const openAddModal = () => {
        setTaskToEdit(null);
        setIsReadOnly(false);
        setCharges([]);
        setIsAddModalOpen(true);
    };

    // Filtering & Sorting
    const filteredTasks = useMemo(() => {
        return tasks.filter(task => {
            const matchesSearch = task.task.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (task.product_name && task.product_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (task.product_sku && task.product_sku.toLowerCase().includes(searchTerm.toLowerCase()));
            const matchesStatus = filterStatus === 'All' || task.status === filterStatus;
            const matchesGroup = filterGroup === 'All' || (filterGroup === 'None' ? !task.group_name : task.group_name === filterGroup);
            return matchesSearch && matchesStatus && matchesGroup;
        });
    }, [tasks, searchTerm, filterStatus, filterGroup]);

    const { items: sortedTasks, requestSort, sortConfig } = useSortableData(filteredTasks, { key: 'due_date', direction: 'ascending' });

    const getStatusColor = (statusName) => {
        const status = statuses.find(s => s.name === statusName);
        return status ? status.hex_code : 'stone';
    };

    return (
        <div className="fixed inset-0 bg-stone-950 z-40 flex flex-col animate-in fade-in duration-200">
            {/* Header */}
            <div className="h-16 border-b border-stone-800 flex items-center justify-between px-6 bg-stone-900/50 backdrop-blur-sm">
                <div className="flex items-center gap-4">
                    <h1 className="text-xl font-bold text-white flex items-center gap-2">
                        <CheckCircle className="text-blue-500" /> Global Task Overview
                    </h1>
                    <span className="px-2 py-0.5 rounded-full bg-stone-800 text-stone-400 text-xs font-medium border border-stone-700">
                        {tasks.length} Tasks
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <GhostButton onClick={() => setIsGroupModalOpen(true)}>
                        <Settings size={16} /> Manage Groups
                    </GhostButton>
                    <button
                        onClick={openAddModal}
                        className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                    >
                        <Plus size={18} /> Add Task
                    </button>
                    <button onClick={onClose} className="ml-4 text-stone-500 hover:text-white transition-colors">
                        Close
                    </button>
                </div>
            </div>

            {/* Toolbar */}
            <div className="p-6 pb-0 flex gap-4 items-center">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" size={16} />
                    <input
                        type="text"
                        placeholder="Search tasks, products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-stone-900 border border-stone-700 rounded-lg pl-10 pr-4 py-2 text-stone-300 focus:outline-none focus:border-blue-500"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <Filter size={16} className="text-stone-500" />
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="bg-stone-900 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-300 focus:outline-none"
                    >
                        <option value="All">All Statuses</option>
                        {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>

                    <select
                        value={filterGroup}
                        onChange={(e) => setFilterGroup(e.target.value)}
                        className="bg-stone-900 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-300 focus:outline-none"
                    >
                        <option value="All">All Groups</option>
                        <option value="None">No Group</option>
                        {groups.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 p-6 overflow-hidden">
                <div className="h-full bg-stone-900/50 border border-stone-800 rounded-xl overflow-hidden flex flex-col">
                    <div className="overflow-y-auto flex-1">
                        <table className="w-full text-left border-collapse">
                            <thead className="sticky top-0 z-10 bg-stone-950">
                                <tr>
                                    <th className="w-12 px-4 py-3 border-b border-stone-800 text-stone-500 font-mono text-xs text-center">#</th>
                                    <th className="w-10 px-4 py-3 border-b border-stone-800"></th>
                                    <SortableHeader label="Task" sortKey="task" currentSort={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Group" sortKey="group_name" currentSort={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Context" sortKey="product_name" currentSort={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Due Date" sortKey="due_date" currentSort={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={requestSort} />
                                    <th className="px-4 py-3 border-b border-stone-800 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-800">
                                {sortedTasks.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="p-12 text-center text-stone-500">
                                            No tasks found matching your filters.
                                        </td>
                                    </tr>
                                ) : (
                                    sortedTasks.map((task, index) => (
                                        <tr key={task.id} className="hover:bg-stone-800/30 transition-colors group">
                                            <td className="px-4 py-3 text-center text-stone-600 font-mono text-xs">
                                                {index + 1}
                                            </td>
                                            <td className="px-4 py-3">
                                                <button
                                                    onClick={() => handleToggleStatus(task)}
                                                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${task.status === 'Completed'
                                                        ? 'bg-green-500 border-green-500 text-white'
                                                        : 'border-stone-600 hover:border-blue-500'
                                                        }`}
                                                >
                                                    {task.status === 'Completed' && <CheckCircle size={12} />}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-stone-200">{task.task}</div>
                                                {task.notes && <div className="text-xs text-stone-500 mt-0.5 truncate max-w-md">{task.notes}</div>}
                                            </td>
                                            <td className="px-4 py-3">
                                                {task.group_name ? (
                                                    <span
                                                        className="px-2 py-0.5 rounded text-xs font-medium border"
                                                        style={{
                                                            backgroundColor: task.group_color ? `${task.group_color}20` : '#333',
                                                            borderColor: task.group_color ? `${task.group_color}40` : '#444',
                                                            color: task.group_color || '#aaa'
                                                        }}
                                                    >
                                                        {task.group_name}
                                                    </span>
                                                ) : (
                                                    <span className="text-stone-600 text-xs">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {task.product_sku ? (
                                                    <div className="flex flex-col">
                                                        <div className="flex items-center gap-1 text-sm text-stone-300">
                                                            <Box size={12} className="text-stone-500" />
                                                            {task.product_model || task.product_sku}
                                                        </div>
                                                        {task.charge_batch && (
                                                            <div className="flex items-center gap-1 text-xs text-stone-500 ml-4">
                                                                <Layers size={10} />
                                                                {task.charge_batch}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-stone-500 text-xs italic">Global</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-stone-400">
                                                {task.due_date ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <Calendar size={12} />
                                                        {new Date(task.due_date).toLocaleDateString()}
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge color={getStatusColor(task.status)}>{task.status}</Badge>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => openEditModal(task, true)}
                                                        className="h-8 w-8 flex items-center justify-center rounded border border-stone-700 text-stone-400 hover:text-blue-400 hover:border-blue-500 hover:bg-stone-800 transition-all"
                                                        title="View Details"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => openEditModal(task)}
                                                        className="h-8 w-8 flex items-center justify-center rounded border border-stone-700 text-stone-400 hover:text-yellow-400 hover:border-yellow-500 hover:bg-stone-800 transition-all"
                                                        title="Edit Task"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteTask(task.id)}
                                                        className="h-8 w-8 flex items-center justify-center rounded border border-stone-700 text-stone-400 hover:text-red-400 hover:border-red-500 hover:bg-red-900/20 transition-all"
                                                        title="Delete Task"
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <ToDoAddModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onAdd={handleAddTodo}
                onEdit={handleEditTodo}
                taskToEdit={taskToEdit}
                isReadOnly={isReadOnly}
                groups={groups}
                charges={charges}
                statuses={statuses}
                products={products}
                onProductSelect={handleProductSelect}
            />

            <ToDoGroupManagementModal
                isOpen={isGroupModalOpen}
                onClose={() => setIsGroupModalOpen(false)}
                groups={groups}
                onAdd={handleAddGroup}
                onUpdate={handleUpdateGroup}
                onDelete={handleDeleteGroup}
            />

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                isDestructive={confirmModal.isDestructive}
            />
        </div>
    );
};

export default GlobalTasksView;
